import { type IQuaternionLike } from "../Maths/math.like";
import { Clamp } from "../Maths/math.scalar.functions";
import { Matrix, Quaternion, Vector2, Vector3 } from "../Maths/math.vector.pure";
import { Vector3Dot, Vector4Dot } from "../Maths/math.vector.functions";
import { type DeepImmutable } from "../types";

// *** NOTE ***
// These functions should ideally go in math.vector.functions.ts, but they require math.vector.ts to
// be imported which is big. To avoid the larger bundle size, they are kept inside flow graph for now.

/**
 * Implementation-defined threshold used by the slerp and up/forward quaternion operations
 * to detect near-zero lengths and (anti)parallel vectors.
 */
const SlerpEpsilon = 1e-6;

/**
 * Returns a unit vector perpendicular to the provided vector.
 * @param v the input vector (does not need to be unit length)
 * @returns a unit vector perpendicular to `v`
 */
function GetAnyPerpendicularVector(v: DeepImmutable<Vector3>): Vector3 {
    const absX = Math.abs(v.x);
    const absY = Math.abs(v.y);
    const absZ = Math.abs(v.z);
    // Cross with whichever cardinal axis is least aligned with `v` to avoid a degenerate cross product.
    let other: DeepImmutable<Vector3>;
    if (absX <= absY && absX <= absZ) {
        other = Vector3.RightReadOnly;
    } else if (absY <= absZ) {
        other = Vector3.UpReadOnly;
    } else {
        other = Vector3.LeftHandedForwardReadOnly;
    }
    return Vector3.Cross(v, other).normalize();
}

/**
 * Returns the angle in radians between two quaternions
 * @param q1 defines the first quaternion
 * @param q2 defines the second quaternion
 * @returns the angle in radians between the two quaternions
 */
export function GetAngleBetweenQuaternions(q1: DeepImmutable<IQuaternionLike>, q2: DeepImmutable<IQuaternionLike>): number {
    return Math.acos(Clamp(Vector4Dot(q1, q2), -1, 1)) * 2;
}

/**
 * Creates a quaternion from two direction vectors
 * @param a defines the first direction vector
 * @param b defines the second direction vector
 * @returns the target quaternion
 */
export function GetQuaternionFromDirections<T extends Vector3>(a: DeepImmutable<T>, b: DeepImmutable<T>): Quaternion {
    const result = new Quaternion();
    GetQuaternionFromDirectionsToRef(a, b, result);
    return result;
}

/**
 * Creates a quaternion from two direction vectors
 * @param a defines the first direction vector
 * @param b defines the second direction vector
 * @param result defines the target quaternion
 * @returns the target quaternion
 */
export function GetQuaternionFromDirectionsToRef<T extends Vector3, ResultT extends Quaternion>(a: DeepImmutable<T>, b: DeepImmutable<T>, result: ResultT): ResultT {
    const dot = Vector3Dot(a, b);
    if (Number.isFinite(dot) && dot > 1 - SlerpEpsilon) {
        result.copyFromFloats(0, 0, 0, 1);
        return result;
    }
    if (Number.isFinite(dot) && dot < -1 + SlerpEpsilon) {
        const axis = GetAnyPerpendicularVector(a);
        result.copyFromFloats(axis.x, axis.y, axis.z, 0);
        return result;
    }

    const axis = Vector3.Cross(a, b).normalize();
    const axisScale = Math.sqrt(0.5 - 0.5 * dot);
    result.copyFromFloats(axis.x * axisScale, axis.y * axisScale, axis.z * axisScale, Math.sqrt(0.5 + 0.5 * dot));
    return result;
}

/**
 * Spherical linear interpolation between two 2D vectors.
 * NaN and infinity values are propagated through the arithmetic.
 * @param a the first vector
 * @param b the second vector
 * @param c the (unclamped) interpolation coefficient
 * @returns the interpolated 2D vector
 */
export function GetVector2Slerp(a: DeepImmutable<Vector2>, b: DeepImmutable<Vector2>, c: number): Vector2 {
    const lengthA = Math.sqrt(a.x * a.x + a.y * a.y);
    const lengthB = Math.sqrt(b.x * b.x + b.y * b.y);
    // If either vector is (close to) zero length the rotation is undefined; fall back to a linear interpolation.
    if (lengthA < SlerpEpsilon || lengthB < SlerpEpsilon) {
        return new Vector2((1 - c) * a.x + c * b.x, (1 - c) * a.y + c * b.y);
    }
    const aHatX = a.x / lengthA;
    const aHatY = a.y / lengthA;
    const bHatX = b.x / lengthB;
    const bHatY = b.y / lengthB;
    let theta = Math.acos(Clamp(aHatX * bHatX + aHatY * bHatY, -1, 1));
    if (aHatX * bHatY - aHatY * bHatX < 0) {
        theta = -theta;
    }
    const length = (1 - c) * lengthA + c * lengthB;
    const cosCTheta = Math.cos(c * theta);
    const sinCTheta = Math.sin(c * theta);
    return new Vector2((aHatX * cosCTheta - aHatY * sinCTheta) * length, (aHatX * sinCTheta + aHatY * cosCTheta) * length);
}

/**
 * Spherical linear interpolation between two 3D vectors.
 * NaN and infinity values are propagated through the arithmetic.
 * @param a the first vector
 * @param b the second vector
 * @param c the (unclamped) interpolation coefficient
 * @returns the interpolated 3D vector
 */
export function GetVector3Slerp(a: DeepImmutable<Vector3>, b: DeepImmutable<Vector3>, c: number): Vector3 {
    const lengthA = a.length();
    const lengthB = b.length();
    const lerp = () => new Vector3((1 - c) * a.x + c * b.x, (1 - c) * a.y + c * b.y, (1 - c) * a.z + c * b.z);
    // If either vector is (close to) zero length the rotation is undefined; fall back to a linear interpolation.
    if (lengthA < SlerpEpsilon || lengthB < SlerpEpsilon) {
        return lerp();
    }
    const aHat = new Vector3(a.x / lengthA, a.y / lengthA, a.z / lengthA);
    const bHat = new Vector3(b.x / lengthB, b.y / lengthB, b.z / lengthB);
    const dot = Vector3Dot(aHat, bHat);
    // Parallel vectors share a direction; a linear interpolation already produces the correct result.
    if (dot > 1 - SlerpEpsilon) {
        return lerp();
    }
    let rotationAxis: Vector3;
    if (dot < -1 + SlerpEpsilon) {
        // Anti-parallel vectors: any axis perpendicular to aHat is a valid rotation axis.
        rotationAxis = GetAnyPerpendicularVector(aHat);
    } else {
        rotationAxis = Vector3.Cross(aHat, bHat).normalize();
    }
    const angle = c * Math.acos(Clamp(dot, -1, 1));
    const rotation = Quaternion.RotationAxis(rotationAxis, angle);
    const length = (1 - c) * lengthA + c * lengthB;
    return aHat.applyRotationQuaternion(rotation).scaleInPlace(length);
}

/**
 * Creates a quaternion from the specified up and forward directions, as defined by the
 * up/forward quaternion operation. Both inputs are assumed to be unit length.
 * @param up the up direction
 * @param forward the forward direction
 * @returns the rotation quaternion
 */
export function GetQuaternionFromUpForward(up: DeepImmutable<Vector3>, forward: DeepImmutable<Vector3>): Quaternion {
    const r = new Vector3(forward.x, forward.y, forward.z);
    let s = Vector3.Cross(up, r);
    if (s.lengthSquared() < SlerpEpsilon * SlerpEpsilon) {
        // up and forward are colinear; pick any unit vector perpendicular to forward.
        s = GetAnyPerpendicularVector(r);
    } else {
        s.normalize();
    }
    const t = Vector3.Cross(r, s);
    // Build the rotation matrix with columns s, t and r (Babylon matrices are column-major) and convert it.
    const matrix = Matrix.FromValues(s.x, s.y, s.z, 0, t.x, t.y, t.z, 0, r.x, r.y, r.z, 0, 0, 0, 0, 1);
    return Quaternion.FromRotationMatrix(matrix);
}

/**
 * The rotation orders accepted by the Euler-angle quaternion operation
 * (and {@link GetQuaternionFromEulerAngles}). The default order is `yxz`.
 */
export const QuaternionEulerAngleOrders = ["xyz", "xzy", "yxz", "yzx", "zxy", "zyx"] as const;

/**
 * Builds a rotation quaternion from three Tait–Bryan intrinsic Euler angles applied in the
 * specified order.
 *
 * Babylon only exposes the `yxz` order natively (via `Quaternion.RotationYawPitchRoll`), so the
 * result is composed from the individual per-axis rotations to support every order. For an
 * intrinsic order `o1o2o3` the result is the Hamilton product `q(o1) * q(o2) * q(o3)`, where each
 * `q(axis)` is a rotation about that axis; this matches the corresponding reference intrinsic
 * Tait–Bryan rotation matrices. NaN and infinite angle inputs propagate into the result.
 * @param order the rotation order, one of {@link QuaternionEulerAngleOrders}; any other value uses the default `yxz`
 * @param x rotation around the X axis, in radians
 * @param y rotation around the Y axis, in radians
 * @param z rotation around the Z axis, in radians
 * @returns the composed rotation quaternion
 */
export function GetQuaternionFromEulerAngles(order: string, x: number, y: number, z: number): Quaternion {
    const qx = Quaternion.RotationAxis(Vector3.RightReadOnly, x);
    const qy = Quaternion.RotationAxis(Vector3.UpReadOnly, y);
    const qz = Quaternion.RotationAxis(Vector3.LeftHandedForwardReadOnly, z);
    // `a.multiplyInPlace(b)` computes the Hamilton product `a * b` in place and returns `a`.
    switch (order) {
        case "xyz":
            return qx.multiplyInPlace(qy).multiplyInPlace(qz);
        case "xzy":
            return qx.multiplyInPlace(qz).multiplyInPlace(qy);
        case "yzx":
            return qy.multiplyInPlace(qz).multiplyInPlace(qx);
        case "zxy":
            return qz.multiplyInPlace(qx).multiplyInPlace(qy);
        case "zyx":
            return qz.multiplyInPlace(qy).multiplyInPlace(qx);
        case "yxz":
        default:
            // Default order.
            return qy.multiplyInPlace(qx).multiplyInPlace(qz);
    }
}
